import { get } from '../shared/get';
import type { Auth0Options, GlobalImport } from '../shared/interfaces';
import { toFeedback } from '../shared/to-feedback';

import { afterSuccessAction } from './shared/after-success-action';
import { FormWidgetBase } from './shared/form-widget-base';
import { getAuth0Client } from './shared/get-auth0-client';
import { recaptchaReady } from './shared/recaptcha-ready';
import { request, requestWithRecaptchaV3 } from './shared/request';
import { selectorToRecurlyFieldMap } from './shared/selector-to-recurly-field-map';
import { accountInfoLabels, addressInfoLabels, addressInfoOptionalLabels } from './shared/selectors';
import { getAuth0AuthenticatedClient } from './shared/setup-auth0';

class UpdateBillingInfoWidget extends FormWidgetBase {
    override requiredImports: GlobalImport[] = [`auth0`, `recaptchaV3`, `recurly`];
    override requiredInfo = [...accountInfoLabels, ...addressInfoLabels];
    override optionalInfo = [...addressInfoOptionalLabels];

    override async afterConnected() {
        const [auth0Script] = await this.importing;
        if (!auth0Script) {
            this.feedback.innerHTML += `Your browser has failed to load script responsible for authorization.`;
            return;
        }

        const widgetConfig = this.config as Auth0Options;
        let accessToken: string | undefined;

        if (widgetConfig.auth0 !== false) {
            const auth0Client = await getAuth0AuthenticatedClient().catch((err) => {
                toFeedback([err.message], this.feedback);
                throw err;
            });
            accessToken = await auth0Client.getTokenSilently();
        }

        Object.entries(this.elements).forEach(([selector, element]) => {
            if (!element.input) {
                return;
            }

            element.input.disabled = true;
        });

        const getAccountResponse = await request({
            apiUrl: window.mh.apiUrl,
            endpoint: `/v1/programs/${window.mh.programSlug}/my-account`,
            method: `GET`,
            ...(accessToken && {
                headers: {
                    authorization: `Bearer ${accessToken}`,
                },
            }),
        });

        const json = await getAccountResponse.json();

        if (!getAccountResponse.ok) {
            throw new Error(json.error.message ?? json.error);
        }

        Object.entries(this.elements).forEach(([selector, element]) => {
            if (!element.input) {
                return;
            }

            const recurlyField = (selectorToRecurlyFieldMap as any)[selector];
            if (!recurlyField) {
                element.input.disabled = false;
                return;
            }

            const value = get(json, recurlyField);
            if (!value) {
                element.input.disabled = false;
                return;
            }

            element.input.value = value;
            element.input.disabled = false;

            if (element.input instanceof HTMLSelectElement) {
                element.input.dispatchEvent(new Event(`change`));
            }
        });

        this.button.disabled = false;
        this.loader.classList.add(`invisible`);
    }

    async submit(info: any) {
        const widgetConfig = this.config as Auth0Options;
        let accessToken: string | undefined;

        if (widgetConfig.auth0 !== false) {
            const auth0Client = await getAuth0Client(this.publicConfig);
            accessToken = await auth0Client.getTokenSilently();
        }

        const data = {
            address: {
                city: info.city,
                country: info.country,
                postal_code: info.postal_code,
                region: info.region,
                street1: info.street1,
                ...(info.phone && {
                    phone: info.phone,
                }),
                ...(info.street2 && {
                    street2: info.street2,
                }),
            },
            email: info.email,
            first_name: info.first_name,
            last_name: info.last_name,
        };

        const recurlyToken = await window.mhRecurly.tokenAsync().catch(() => {
            // We allow to update other information without credit card.
        });

        await recaptchaReady();
        const updateAccountResponse = await requestWithRecaptchaV3({
            apiUrl: window.mh.apiUrl,
            data: {
                ...data,
                billing_info: {
                    ...(recurlyToken && { token_id: recurlyToken.id }),
                },
            },
            endpoint: `/v1/programs/${window.mh.programSlug}/my-account`,
            method: `PUT`,
            recaptchaV3SiteKey: this.publicConfig.integrations.recaptchaV3.siteKey,
            ...(accessToken && {
                headers: {
                    authorization: `Bearer ${accessToken}`,
                },
            }),
        });

        if (!updateAccountResponse.ok) {
            const json = await updateAccountResponse.json();
            throw new Error(json.error.message ?? json.error);
        }

        if (`afterSuccess` in this.config) {
            afterSuccessAction(this.config.afterSuccess, info);
        }
    }
}

customElements.define(`mh-update-billing-info`, UpdateBillingInfoWidget);

export default UpdateBillingInfoWidget;
